"use client"

import { useState } from "react"
import { Button } from "./components/ui/button"
import { Card, CardContent } from "./components/ui/card"
import { Label } from "./components/ui/label"
import { Textarea } from "./components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select"
import { RadioGroup, RadioGroupItem } from "./components/ui/radio-group"
import { Progress } from "./components/ui/progress"
import { ChevronLeft, ChevronRight, Heart, CheckCircle } from "lucide-react"
import "../src/styles/globals.css"

const initialFormData = {
  department: "",
  happyWorking: { selectedOption: "", comment: "" },
  feelProud: { selectedOption: "", comment: "" },
  recommendWorkplace: { selectedOption: "", comment: "" },
  safeComfortable: { selectedOption: "", comment: "" },
  hasTools: { selectedOption: "", comment: "" },
  teamwork: { selectedOption: "", comment: "" },
  managerAppreciation: { selectedOption: "", comment: "" },
  managerSupport: { selectedOption: "", comment: "" },
  clearCommunication: { selectedOption: "", comment: "" },
  managementListens: { selectedOption: "", comment: "" },
  fairDecisions: { selectedOption: "", comment: "" },
  managementFeedback: "",
  learningOpportunities: { selectedOption: "", comment: "" },
  trainingHelpful: { selectedOption: "", comment: "" },
  fairPerformanceJudgment: { selectedOption: "", comment: "" },
  salarySatisfaction: { selectedOption: "", comment: "" },
  benefitsSatisfaction: { selectedOption: "", comment: "" },
  workRewardedProperly: { selectedOption: "", comment: "" },
  reasonableHours: { selectedOption: "", comment: "" },
  adequateBreaks: { selectedOption: "", comment: "" },
  workLifeBalance: { selectedOption: "", comment: "" },
  likeMost: "",
  encouraged: "",
  discouraginginwork: "",
  improvements: "",
}

const departments = [
  "Doctor",
  "Staff",
]

const questionOptions = {
  happyWorking: ["Very unhappy", "Unhappy", "Neutral (okay)", "Happy", "Very happy"],
  feelProud: ["Not at all proud", "A little proud", "Neutral (sometimes proud)", "Proud", "Very proud"],
  recommendWorkplace: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  safeComfortable: ["Not safe at all", "Sometimes unsafe", "Neutral (okay)", "Safe", "Very safe and comfortable"],
  hasTools: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  teamwork: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  managerAppreciation: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  managerSupport: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  clearCommunication: ["Very unclear", "Sometimes unclear", "Neutral (okay)", "Clear", "Very clear"],
  managementListens: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  fairDecisions: ["Very unfair", "Unfair", "Neutral", "Fair", "Very fair"],
  learningOpportunities: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  trainingHelpful: ["Not at all helpful", "Slightly helpful", "Neutral", "Helpful", "Very helpful"],
  fairPerformanceJudgment: ["Very unfair", "Unfair", "Neutral", "Fair", "Very fair"],
  salarySatisfaction: ["Very dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very satisfied"],
  benefitsSatisfaction: ["Very dissatisfied", "Dissatisfied", "Neutral", "Satisfied", "Very satisfied"],
  workRewardedProperly: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  reasonableHours: ["Very unreasonable", "Unreasonable", "Neutral (sometimes okay)", "Reasonable", "Very reasonable"],
  adequateBreaks: ["Never", "Rarely", "Sometimes", "Often", "Always"],
  workLifeBalance: ["Never", "Rarely", "Sometimes", "Often", "Always"],
}

const steps = [
  { id: "department", title: "Department", type: "select" },
  { id: "happyWorking", title: "Are you happy working at Pranaam Hospitals?", type: "radio-comment", category: "General" },
  { id: "feelProud", title: "Do you feel proud to work here?", type: "radio-comment", category: "General" },
  { id: "recommendWorkplace", title: "Will you recommend Pranaam Hospitals as a good workplace to others?", type: "radio-comment", category: "General" },
  { id: "safeComfortable", title: "Do you feel safe and comfortable at work?", type: "radio-comment", category: "Work Environment" },
  { id: "hasTools", title: "Do you have the tools and things you need to do your job well?", type: "radio-comment", category: "Work Environment" },
  { id: "teamwork", title: "Do people in your department work well as a team?", type: "radio-comment", category: "Work Environment" },
  { id: "managerAppreciation", title: "Does your HOD/Manager appreciate your work?", type: "radio-comment", category: "Management & Leadership" },
  { id: "managerSupport", title: "Does your HOD/Manager support you in solving problems at work?", type: "radio-comment", category: "Management & Leadership" },
  { id: "clearCommunication", title: "Do you get clear instructions and communication from your HOD/Manager?", type: "radio-comment", category: "Management & Leadership" },
  { id: "managementListens", title: "Do you feel the hospital management listens to staff concerns?", type: "radio-comment", category: "Management & Leadership" },
  { id: "fairDecisions", title: "Do you think hospital management makes fair and transparent decisions?", type: "radio-comment", category: "Management & Leadership" },
  { id: "managementFeedback", title: "What feedback do you want to give about your HOD or hospital management?", type: "textarea", category: "Management & Leadership" },
  { id: "learningOpportunities", title: "Do you get chances to learn new skills and grow in your career?", type: "radio-comment", category: "Growth & Training" },
  { id: "trainingHelpful", title: "Are the training programs helpful for your job?", type: "radio-comment", category: "Growth & Training" },
  { id: "fairPerformanceJudgment", title: "Do you think your work performance is judged fairly?", type: "radio-comment", category: "Growth & Training" },
  { id: "salarySatisfaction", title: "Are you satisfied with your salary?", type: "radio-comment", category: "Pay & Benefits" },
  { id: "benefitsSatisfaction", title: "Are you satisfied with other benefits (insurance, leaves, allowances)?", type: "radio-comment", category: "Pay & Benefits" },
  { id: "workRewardedProperly", title: "Do you feel good work is rewarded properly?", type: "radio-comment", category: "Pay & Benefits" },
  { id: "reasonableHours", title: "Are your duty hours and shifts reasonable?", type: "radio-comment", category: "Work-Life Balance" },
  { id: "adequateBreaks", title: "Do you get enough breaks during your work shift?", type: "radio-comment", category: "Work-Life Balance" },
  { id: "workLifeBalance", title: "Do you feel you can balance your work and personal life?", type: "radio-comment", category: "Work-Life Balance" },
  { id: "likeMost", title: "What do you like the most about working at Pranaam Hospitals?", type: "textarea", category: "Open Feedback" },
  { id: "encouraged", title: "Name one person who has helped and encouraged you in your work?", type: "textarea", category: "Open Feedback" },
  { id: "discouraginginwork", title: "Name one person who has not been helpful and has discouraged you in your work?", type: "textarea", category: "Open Feedback" },
  { id: "improvements", title: "What can we improve in your department or in the hospital overall?", type: "textarea", category: "Open Feedback" },
]

export default function EmployeeSurveyForm() {
  const [formData, setFormData] = useState(initialFormData)
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const currentStepData = steps[currentStep]
  const progress = ((currentStep + 1) / steps.length) * 100

  const updateOption = (field, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], selectedOption },
    }))
  }

  const updateComment = (field, comment) => {
    setFormData(prev => ({
      ...prev,
      [field]: { ...prev[field], comment },
    }))
  }

  const updateTextarea = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))
  }

  const nextStep = () => {
    if (canProceed() && currentStep < steps.length - 1) setCurrentStep(prev => prev + 1)
  }

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1)
  }

  // All fields are mandatory
  const canProceed = () => {
    if (currentStepData.type === "select") {
      return formData[currentStepData.id] !== ""
    }
    if (currentStepData.type === "radio-comment") {
      return formData[currentStepData.id].selectedOption !== ""
    }
    if (currentStepData.type === "textarea") {
      return formData[currentStepData.id].trim() !== ""
    }
    return false
  }
  const handleSubmit = async () => {
    const payload = {
      department: formData.department || null,
      happy_working_option: formData.happyWorking.selectedOption || null,
      happy_working_comment: formData.happyWorking.comment || null,
      feel_proud_option: formData.feelProud.selectedOption || null,
      feel_proud_comment: formData.feelProud.comment || null,
      recommend_workplace_option: formData.recommendWorkplace.selectedOption || null,
      recommend_workplace_comment: formData.recommendWorkplace.comment || null,
      safe_comfortable_option: formData.safeComfortable.selectedOption || null,
      safe_comfortable_comment: formData.safeComfortable.comment || null,
      has_tools_option: formData.hasTools.selectedOption || null,
      has_tools_comment: formData.hasTools.comment || null,
      teamwork_option: formData.teamwork.selectedOption || null,
      teamwork_comment: formData.teamwork.comment || null,
      manager_appreciation_option: formData.managerAppreciation.selectedOption || null,
      manager_appreciation_comment: formData.managerAppreciation.comment || null,
      manager_support_option: formData.managerSupport.selectedOption || null,
      manager_support_comment: formData.managerSupport.comment || null,
      clear_communication_option: formData.clearCommunication.selectedOption || null,
      clear_communication_comment: formData.clearCommunication.comment || null,
      management_listens_option: formData.managementListens.selectedOption || null,
      management_listens_comment: formData.managementListens.comment || null,
      fair_decisions_option: formData.fairDecisions.selectedOption || null,
      fair_decisions_comment: formData.fairDecisions.comment || null,
      management_feedback: formData.managementFeedback || null,
      learning_opportunities_option: formData.learningOpportunities.selectedOption || null,
      learning_opportunities_comment: formData.learningOpportunities.comment || null,
      training_helpful_option: formData.trainingHelpful.selectedOption || null,
      training_helpful_comment: formData.trainingHelpful.comment || null,
      fair_performance_judgment_option: formData.fairPerformanceJudgment.selectedOption || null,
      fair_performance_judgment_comment: formData.fairPerformanceJudgment.comment || null,
      salary_satisfaction_option: formData.salarySatisfaction.selectedOption || null,
      salary_satisfaction_comment: formData.salarySatisfaction.comment || null,
      benefits_satisfaction_option: formData.benefitsSatisfaction.selectedOption || null,
      benefits_satisfaction_comment: formData.benefitsSatisfaction.comment || null,
      work_rewarded_properly_option: formData.workRewardedProperly.selectedOption || null,
      work_rewarded_properly_comment: formData.workRewardedProperly.comment || null,
      reasonable_hours_option: formData.reasonableHours.selectedOption || null,
      reasonable_hours_comment: formData.reasonableHours.comment || null,
      adequate_breaks_option: formData.adequateBreaks.selectedOption || null,
      adequate_breaks_comment: formData.adequateBreaks.comment || null,
      work_life_balance_option: formData.workLifeBalance.selectedOption || null,
      work_life_balance_comment: formData.workLifeBalance.comment || null,
      like_most: formData.likeMost || null,
      helped_encouraged: formData.encouraged || null,
      discouraging_in_work: formData.discouraginginwork || null,
      improvements: formData.improvements || null,
      created_at: new Date().toISOString().split("T")[0],
    };

    const response = await fetch(
      "https://feedback.pranaamhospitals.com/index.php/EmployeeSurvey/employee_survey",
      {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(payload).toString(),
      }
    );

    if (response.ok) setIsSubmitted(true);
  };





  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="p-8 text-center space-y-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">Thank You!</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your feedback has been submitted successfully. Your input helps us improve Pranaam Hospitals for everyone.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="flex items-center justify-center mb-6">
            <img src="/images/pranaam-logo.png" alt="Pranaam Hospitals" className="h-12 w-auto" />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Step {currentStep + 1} of {steps.length}</span>
              <span>{Math.round(progress)}% Complete</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            {currentStepData.category && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary mb-6">{currentStepData.category}</span>
            )}

            <h1 className="text-2xl font-bold mb-8">{currentStepData.title}</h1>

            {/* Render select, radio-comment, textarea */}
            {currentStepData.type === "select" && (
              <Select value={formData.department} onValueChange={value => setFormData(prev => ({ ...prev, department: value }))}>
                <SelectTrigger className="h-12 text-base">
                  <SelectValue placeholder="Choose your department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => <SelectItem key={dept} value={dept}>{dept}</SelectItem>)}
                </SelectContent>
              </Select>
            )}

            {currentStepData.type === "radio-comment" && (
              <div className="space-y-4">
                <RadioGroup value={formData[currentStepData.id].selectedOption} onValueChange={value => updateOption(currentStepData.id, value)} className="space-y-3">
                  {questionOptions[currentStepData.id].map(option => (
                    <div key={option} className="flex items-center space-x-3 rounded-lg hover:bg-accent/50 transition-colors">
                      <RadioGroupItem value={option} id={option} />
                      <Label htmlFor={option} className="text-base cursor-pointer">{option}</Label>
                    </div>
                  ))}
                </RadioGroup>
                <Textarea placeholder="Additional comments (optional)" value={formData[currentStepData.id].comment} onChange={e => updateComment(currentStepData.id, e.target.value)} className="min-h-[80px] resize-none" />
              </div>
            )}

            {currentStepData.type === "textarea" && (
              <Textarea placeholder="Share your thoughts..." value={formData[currentStepData.id]} onChange={e => updateTextarea(currentStepData.id, e.target.value)} className="min-h-[120px]" />
            )}
          </CardContent>
        </Card>

        <div className="flex items-center justify-between mt-8">
          <Button onClick={prevStep} disabled={currentStep === 0} variant="outline" className="flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> Previous</Button>
          {currentStep === steps.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed()}
              className={`flex items-center gap-2 px-8 ${!canProceed() ? 'opacity-50 cursor-not-allowed' : '!bg-primary !text-primary-foreground !opacity-100 hover:!bg-primary/90'}`}
            >
              Submit Feedback <Heart className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-2 px-8 ${!canProceed() ? 'opacity-50 cursor-not-allowed' : '!bg-primary !text-primary-foreground !opacity-100 hover:!bg-primary/90'}`}
            >
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          )}

        </div>
      </div>
    </div>
  )
}
